import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import CtaSection from '../components/CtaSection';
import { getOptimizedImageUrl } from '../lib/cms';

export default function HowWeWorkPage() {
  const processSteps = [
    {
      step: '01',
      title: 'Indledende online kontakt & forventningsafstemning',
      desc: 'Vi starter med en uforpligtende samtale over telefon eller online, hvor vi afdækker jeres forretning, målgruppe, nuværende profiler og specifikke ønsker.',
      details: [
        'Gennemgang af jeres nuværende sociale medier og målsætninger',
        'Afdækning af hvad I ønsker hjælp til (redigering, kalender, profilopsætning)',
        'Skræddersyet aftale uden faste prispakker – præcist tilpasset jer',
      ],
    },
    {
      step: '02',
      title: 'Vejledning & inspiration til optagelser (eller besøg)',
      desc: 'Som udgangspunkt fanger I selv hverdagen med smartphone efter vores vejledning – og ved nærmere aftale er vi også fleksible til at komme ud til jer.',
      details: [
        'Konkrete tips til kameravinkler, naturligt lys og rolig føring af telefonen',
        'Idélister og skabeloner til hvad der virker godt i jeres branche',
        'Mulighed for at vi kommer ud og hjælper på adressen ved nærmere aftale',
      ],
    },
    {
      step: '03',
      title: 'I optager & sender råmaterialet online',
      desc: 'I filmer hverdagssekvenserne, når det passer ind i jeres arbejdsgang, og uploader nemt råfilerne direkte til os via Google Drive, Dropbox eller fildeling.',
      details: [
        'Hurtig og ubesværet fildeling uden tekniske barrierer',
        'I behøver ikke sortere eller klippe – vi tager imod råoptagelserne',
        'Løbende online feedback og sparring på jeres optagelser',
      ],
    },
    {
      step: '04',
      title: 'Professionel redigering & content opsætning',
      desc: 'Vi klipper jeres materiale til fængende videoer, tilføjer dynamiske tekster, stærke hooks, trending lyde og optimerer det til præcis de platforme, I ønsker (f.eks. Reels, TikTok, Facebook, LinkedIn eller YouTube).',
      details: [
        'Dynamisk klipning der fastholder seerens opmærksomhed',
        'Tekstning og undertekster, så videoen virker både med og uden lyd',
        'Format og layout tilpasset de kanaler, jeres kunder befinder sig på',
      ],
    },
    {
      step: '05',
      title: 'Postkalender & kontinuerlig synlighed',
      desc: 'Vi kan oprette og vedligeholde en struktureret postkalender, skrive engagerende opslagstekster og planlægge udgivelserne på de bedste tidspunkter på tværs af jeres platforme.',
      details: [
        'Visuel postkalender med fuldt overblik over planlagte udgivelser',
        'Udarbejdelse af fængende captions, hashtags og tags',
        'Altid 0 binding – fleksible vilkår der kan tilpasses måned for måned',
      ],
    },
  ];

  return (
    <>
      <SeoHead
        title="Sådan arbejder vi | SoMe Sparring, Redigering & Synlighed"
        description="Læs om vores strukturerede 5-trins proces: Sparring, I filmer med smartphone, vi redigerer og opbygger synlighed via Meta-postkalenderen. 0 binding."
      />

      {/* Hero */}
      <section className="pt-40 pb-20 md:pt-48 lg:pt-52 md:pb-28 bg-[#FAF8F5] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#EAE4DC] text-xs font-semibold uppercase tracking-widest text-[#4A4640]">
              Sparring · Redigering · Synlighed
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#161514] leading-[1.1]">
              Smidig arbejdsproces fra{' '}
              <span className="text-[#876F50]">råklip til færdigt opslag</span>
            </h1>

            <p className="text-lg sm:text-xl text-[#5C5852] leading-relaxed">
              Vi gør det nemt for virksomheder at opnå reel synlighed på sociale medier. I optager hverdagens autentiske øjeblikke – vi sparrer med jer, klipper indholdet og holder kalenderen i gang.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#161514] text-[#FAF8F5] font-semibold text-base hover:bg-[#2A2926] active:scale-98 transition-all shadow-md"
              >
                <span>Kontakt os for en uforpligtende aftale</span>
                <i className="fa-regular fa-arrow-right text-sm" aria-hidden="true" />
              </Link>
              <span className="text-xs text-[#7A746B] font-medium">
                0 binding • Betal 2 mdr, få 1 gratis
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 md:py-28 bg-white border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16 md:mb-20 space-y-4 text-left">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#161514]">
              Fem trin til kontinuerligt og <br className="hidden sm:inline" />professionelt indhold
            </h2>
            <p className="text-base text-[#5C5852]">
              Hvert skridt er tilrettelagt for at spare jer tid i hverdagen og give jer tryghed.
            </p>
          </div>

          <div className="space-y-16 lg:space-y-24">
            {processSteps.map((p) => (
              <div
                key={p.step}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start border-b border-[#EFEBE4] pb-16 last:border-0 last:pb-0"
              >
                <div className="lg:col-span-4 flex items-center lg:items-start gap-4">
                  <span className="text-5xl lg:text-6xl font-extrabold text-[#C5A880] leading-none">
                    {p.step}
                  </span>
                  <div className="pt-1">
                    <h3 className="text-2xl font-bold text-[#161514]">
                      {p.title}
                    </h3>
                  </div>
                </div>

                <div className="lg:col-span-8 space-y-6">
                  <p className="text-base sm:text-lg text-[#5C5852] leading-relaxed">
                    {p.desc}
                  </p>

                  <div className="pt-2 space-y-3">
                    <h4 className="text-xs uppercase tracking-widest font-bold text-[#8C867D]">
                      Hvad det indebærer:
                    </h4>
                    <ul className="space-y-2.5">
                      {p.details.map((detail, dIdx) => (
                        <li key={dIdx} className="flex items-start gap-2.5 text-sm text-[#4A4640]">
                          <i className="fa-solid fa-check text-[#A8885B] text-xs mt-1 shrink-0" aria-hidden="true" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Online Fordelssammenligning */}
      <section className="py-20 md:py-28 bg-[#FAFAF8] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs uppercase tracking-widest text-[#A8885B] font-bold">
                Hvorfor online model?
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#161514]">
                Autentisk indhold uden forstyrrelser i driften
              </h2>
              <div className="space-y-4 text-[#5C5852] text-base leading-relaxed">
                <p>
                  Traditionelle videoproduktioner med filmhold koster ofte en formue og forstyrrer gæster eller medarbejdere i butikken eller på kontoret. Samtidig foretrækker sociale mediers algoritmer i dag det autentiske, hurtige og nærværende videoindhold.
                </p>
                <p>
                  Med vores online model lærer I selv at filme de rigtige øjeblikke, når de sker i hverdagen. I uploader blot filerne til os – og vi forvandler dem til professionelle, fængende videoer, der udkommer planmæssigt i Meta og driver synlighed, engagement og øget salg.
                </p>
                <p className="font-semibold text-[#161514]">
                  Ingen faste prispakker, 0 binding og fuld fleksibilitet for din forretning.
                </p>
              </div>
            </div>

            <div className="lg:col-span-6 rounded-3xl overflow-hidden shadow-lg border border-[#E5DFD6] aspect-16/10 bg-[#161514]">
              <img
                src={getOptimizedImageUrl(
                  'https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/service-some-guidance.png',
                  { width: 1000, quality: 85, format: 'auto' }
                )}
                alt="Online SoMe vejledning og smartphone optagelse"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Cta */}
      <CtaSection
        title="Vil du have en uforpligtende snak om processen?"
        subtitle="Hør hvordan et online samarbejde kan tilpasses din virksomheds behov. Husk vores kampagne: Betal 2 mdr – få 1 gratis."
        primaryButtonText="Kontakt os i dag"
        primaryButtonLink="/kontakt"
        backgroundImage="https://assets.auxodev.dk/assets/d61c94bc-c5ec-439d-a220-afcc00eef7a0/production-editing-studio.png"
      />
    </>
  );
}
